import React, { useCallback, useEffect, useState } from 'react';
import api from '../../utils/api';
import { bookingUrl } from '../../utils/portalOrigin';

/*
 * "Corporate Code Management" — a port of scale-portal's
 * components/template/Account/components/CouponCode.jsx.
 *
 * Four behaviours from the original are deliberately NOT reproduced, because
 * each of them damages data rather than merely looking different:
 *
 *  - The code cannot be renamed here. Scale allows an inline rename; doing so
 *    breaks every booking link (scale-be truncates a code at the first hyphen
 *    when creating and validating, but not when updating), retroactively
 *    renames every case ever booked under that code, and orphans the email
 *    templates keyed to it. The field is read-only after creation.
 *  - Scale's update endpoint drops hourly rate, deposit and the two party
 *    amounts from its payload, so editing them reports success and saves
 *    nothing. Ours saves them.
 *  - Scale's update forces the half-day and full-day amounts to "0" on every
 *    write, so changing the processing-fee dropdown on an hourly code zeroes
 *    its fixed amounts. Ours only writes what was sent.
 *  - Delete is scoped to the mediator here. Scale's is not.
 *
 * Deleting is a soft delete in both: a case keeps its coupon_id long after the
 * code is retired, and that coupon decided the case's mediation number format.
 */

const COUPON_TYPES = [
  { label: 'Fixed Rate', value: 'fixed' },
  { label: 'Hourly Rate', value: 'hourly' },
];
// No STATUSES list: the Scale portal has no status on a corporate code at all
// (zero references in CouponCode.jsx). New codes are still created with
// status 'active' so the stored row matches what Scale writes.
// Labels match scale-portal's processingFeeOptions verbatim (CouponCode.jsx:36-40)
// so the two screens read identically. 'Yes' means the client pays the card fee.
const PROCESSING_FEE = [
  { label: 'Yes', value: '1' },
  { label: 'No', value: '0' },
  { label: 'Default', value: '-1' },
];

const money = (v) => {
  if (v === null || v === undefined || v === '') return '';
  const n = parseInt(String(v).replace(/[^0-9]/g, ''), 10);
  if (isNaN(n)) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(n);
};
const digits = (v) => String(v ?? '').replace(/[^0-9]/g, '');
const toNum = (v) => (v === '' || v === null || v === undefined ? null : parseInt(v, 10));

// Scale's own control classes, ported into src/styles/index.css.
const control = 'input input-md';
const selectCls = 'select-control';
const cstyle = { fontFamily: 'Inter, sans-serif', color: '#0e1e20' };
const lbl = 'text-black pb-2 flex items-center text-sm';

const emptyForm = () => ({
  coupon_code: '', coupon_type: 'fixed', status: 'active',
  is_processing_fee_by_client: '-1',
  half_day_amount: '', full_day_amount: '', hourly_rate: '', deposit: '',
});

export default function CorporateCodesSection({ mediatorId, userId, notify }) {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    if (!mediatorId) { setLoading(false); return; }
    try {
      setLoading(true);
      const res = await api.get(`/mediators/${mediatorId}/coupons`);
      setCoupons(res.data?.data || []);
    } catch (err) {
      notify?.({ type: 'error', message: err.response?.data?.message || 'Failed to load corporate codes' });
    } finally {
      setLoading(false);
    }
  }, [mediatorId, notify]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setError('');
    if (!form.coupon_code.trim()) { setError('Code is required'); return; }
    const payload = {
      coupon_code: form.coupon_code,
      coupon_type: form.coupon_type,
      status: form.status,
      is_processing_fee_by_client: form.is_processing_fee_by_client,
    };
    if (form.coupon_type === 'fixed') {
      payload.half_day_amount = toNum(form.half_day_amount);
      payload.full_day_amount = toNum(form.full_day_amount);
    } else {
      payload.hourly_rate = toNum(form.hourly_rate);
      payload.deposit = toNum(form.deposit);
    }
    try {
      setBusy(true);
      await api.post(`/mediators/${mediatorId}/coupons`, payload);
      notify?.({ type: 'success', message: 'Corporate code created' });
      setForm(emptyForm());
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create corporate code');
    } finally {
      setBusy(false);
    }
  };

  // Copies the booking link for a code, the way Scale's own Copy URL does.
  // navigator.clipboard needs a secure context; on http it is undefined, so the
  // failure is reported rather than appearing to succeed silently.
  const copyUrl = async (code) => {
    const url = bookingUrl(userId, code);
    if (!url) {
      notify?.({ type: 'error', message: 'No booking link for this mediator yet' });
      return;
    }
    try {
      if (!navigator?.clipboard?.writeText) throw new Error('clipboard unavailable');
      await navigator.clipboard.writeText(url);
      notify?.({ type: 'success', message: 'Corporate Code URL copied to clipboard!' });
    } catch (e) {
      notify?.({ type: 'error', message: `Could not copy. The link is ${url}` });
    }
  };

  const patch = async (id, body) => {
    try {
      await api.put(`/mediators/${mediatorId}/coupons/${id}`, body);
      notify?.({ type: 'success', message: 'Changes Saved' });
      load();
    } catch (err) {
      notify?.({ type: 'error', message: err.response?.data?.message || 'Failed to update corporate code' });
      load();
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/mediators/${mediatorId}/coupons/${id}`);
      notify?.({ type: 'success', message: 'Corporate code removed' });
      setConfirmDelete(null);
      load();
    } catch (err) {
      notify?.({ type: 'error', message: err.response?.data?.message || 'Failed to remove corporate code' });
    }
  };

  const hyphenPreview = form.coupon_code.includes('-') ? form.coupon_code.split('-')[0].trim() : '';

  return (
    <div className="col-span-2">
      <div className="flex flex-col gap-y-1 py-2">
        <h3 className="text-xl font-semibold text-[#0e1e20]" style={{ fontFamily: "'Inter Tight', sans-serif" }}>Corporate Code Management</h3>
        <p className="text-sm font-normal text-[#5b5a64]" style={{ fontFamily: 'Inter, sans-serif' }}>
          Handle corporate for different agencies / customers separately here.
        </p>
      </div>

      <div className="bg-white p-8 w-full rounded-md shadow">
        <div className="flex justify-end">
          <button type="button" onClick={() => { setShowForm((v) => !v); setError(''); }}
            className="button button-solid button-sm"
            style={{ fontFamily: 'Inter, sans-serif', background: '#115e59' }}>
            Create New Code
          </button>
        </div>
        <div className="border-b border-gray-300 mt-3" />

        {error && (
          <div className="mt-4 rounded-md px-4 py-3 text-sm"
            style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontFamily: 'Inter, sans-serif' }}>
            {error}
          </div>
        )}

        {showForm && (
          <div className="mt-4 border border-gray-200 rounded-md p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className={lbl} style={cstyle}>Code</label>
                <input className={control} style={cstyle} placeholder="Enter code"
                  value={form.coupon_code}
                  onChange={(e) => setForm((p) => ({ ...p, coupon_code: e.target.value }))} />
                {hyphenPreview && (
                  <span className="text-[11px] mt-1 block" style={{ color: '#92400e', fontFamily: 'Inter, sans-serif' }}>
                    Will be saved as &ldquo;{hyphenPreview}&rdquo; — booking only ever matches the part before a hyphen.
                  </span>
                )}
                <span className="text-[11px] mt-1 block" style={{ color: '#9ca3af', fontFamily: 'Inter, sans-serif' }}>
                  The code cannot be changed later.
                </span>
              </div>
              <div>
                <label className={lbl} style={cstyle}>Type</label>
                <select className={selectCls} style={cstyle} value={form.coupon_type}
                  onChange={(e) => setForm((p) => ({ ...p, coupon_type: e.target.value }))}>
                  {COUPON_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              {form.coupon_type === 'fixed' ? (
                <>
                  <div>
                    <label className={lbl} style={cstyle}>Half-day Amount</label>
                    <input className={control} style={cstyle} placeholder="Enter Half-day amount"
                      value={money(form.half_day_amount)}
                      onChange={(e) => setForm((p) => ({ ...p, half_day_amount: digits(e.target.value) }))} />
                  </div>
                  <div>
                    <label className={lbl} style={cstyle}>Full-day Amount</label>
                    <input className={control} style={cstyle} placeholder="Enter Full-day amount"
                      value={money(form.full_day_amount)}
                      onChange={(e) => setForm((p) => ({ ...p, full_day_amount: digits(e.target.value) }))} />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className={lbl} style={cstyle}>Hourly Rate</label>
                    <input className={control} style={cstyle} placeholder="Enter Hourly Rate"
                      value={money(form.hourly_rate)}
                      onChange={(e) => setForm((p) => ({ ...p, hourly_rate: digits(e.target.value) }))} />
                  </div>
                  <div>
                    <label className={lbl} style={cstyle}>Deposit</label>
                    <input className={control} style={cstyle} placeholder="Enter Deposit"
                      value={money(form.deposit)}
                      onChange={(e) => setForm((p) => ({ ...p, deposit: digits(e.target.value) }))} />
                  </div>
                </>
              )}
              <div>
                <label className={lbl} style={cstyle}>Processing Fee</label>
                <select className={selectCls} style={cstyle} value={form.is_processing_fee_by_client}
                  onChange={(e) => setForm((p) => ({ ...p, is_processing_fee_by_client: e.target.value }))}>
                  {PROCESSING_FEE.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-4">
              <button type="button" onClick={() => { setShowForm(false); setError(''); }}
                className="button button-default button-sm"
                style={{ fontFamily: 'Inter, sans-serif' }}>Cancel</button>
              <button type="button" onClick={create} disabled={busy}
                className="button button-solid button-sm"
                style={{ fontFamily: 'Inter, sans-serif', background: busy ? '#e5e5e5' : '#115e59', color: busy ? '#9ca3af' : '#fff' }}>
                {busy ? 'Saving…' : 'Add Code'}
              </button>
            </div>
          </div>
        )}

        <div className="mt-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-[#115e59] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : coupons.length === 0 ? (
            <p className="text-sm text-gray-600 py-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              No corporate codes yet.
            </p>
          ) : (
            <div className="w-full divide-y divide-gray-300 space-y-3">
              {coupons.map((c) => (
                <CouponRow key={c.id} coupon={c} onPatch={patch} onAskDelete={() => setConfirmDelete(c)} onCopyUrl={copyUrl} />
              ))}
            </div>
          )}
        </div>
      </div>

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(15,23,42,0.45)' }} role="dialog" aria-modal="true">
          <div className="bg-white rounded-md shadow-lg p-6 mx-4" style={{ maxWidth: 460, width: '100%' }}>
            <h4 className="text-base font-semibold text-[#0e1e20]" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
              Remove &ldquo;{confirmDelete.coupon_code}&rdquo;?
            </h4>
            <p className="text-sm text-gray-600 mt-2" style={{ fontFamily: 'Inter, sans-serif' }}>
              It stops being offered on the booking page. Cases already booked with it keep their
              pricing and their mediation number.
            </p>
            <div className="flex items-center justify-end gap-2 mt-6">
              <button type="button" onClick={() => setConfirmDelete(null)}
                className="button button-default button-sm"
                style={{ fontFamily: 'Inter, sans-serif' }}>Cancel</button>
              <button type="button" onClick={() => remove(confirmDelete.id)}
                className="button button-solid button-sm"
                style={{ fontFamily: 'Inter, sans-serif', background: '#dc2626' }}>Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CouponRow({ coupon, onPatch, onAskDelete, onCopyUrl }) {
  const isSystem = !!coupon.system_coupon_id || coupon.is_system_coupon === true;
  const isHourly = coupon.coupon_type === 'hourly';
  const [local, setLocal] = useState({
    half_day_amount: coupon.half_day_amount ?? '',
    full_day_amount: coupon.full_day_amount ?? '',
    hourly_rate: coupon.hourly_rate ?? '',
    deposit: coupon.deposit ?? '',
    initiating_party: coupon.initiating_party ?? '',
    responding_party: coupon.responding_party ?? '',
  });
  const [localCode, setLocalCode] = useState(coupon.coupon_code || '');
  // Resync when the saved value comes back different from what was typed —
  // the server cuts a code at the first hyphen, so "USDC-2026" is stored as
  // "USDC". Without this the row keeps showing the rejected text and the next
  // blur re-sends it. The row is keyed on coupon.id and never remounts, so the
  // initial useState value alone is not enough.
  useEffect(() => { setLocalCode(coupon.coupon_code || ''); }, [coupon.coupon_code]);

  const blurSave = (field) => {
    const sent = toNum(digits(local[field]));
    if (sent === (coupon[field] ?? null)) return;
    onPatch(coupon.id, { [field]: sent });
  };

  return (
    <div className="pt-3 grid grid-cols-2 md:grid-cols-5 gap-4 gap-6 items-end">
      <div>
        <label className={lbl} style={cstyle}>Corporate Code</label>
        {/* Editable inline, as it is in the Scale portal. Saved on blur so a
            half-typed code is never written. A system-linked code stays locked,
            which is also Scale's behaviour (disabled on coupon.system_coupon_id). */}
        <input
          className={isSystem ? `${control} input-disabled` : control}
          style={isSystem ? { ...cstyle, color: '#6b7280' } : cstyle}
          value={localCode}
          disabled={isSystem}
          readOnly={isSystem}
          onChange={(e) => setLocalCode(e.target.value)}
          onBlur={() => {
            const next = localCode.trim();
            if (!next || next === (coupon.coupon_code || '')) { setLocalCode(coupon.coupon_code || ''); return; }
            onPatch(coupon.id, { coupon_code: next });
          }}
        />
        {isSystem ? (
          <span className="text-[11px] mt-1 block" style={{ color: '#9ca3af', fontFamily: 'Inter, sans-serif' }}>System code</span>
        ) : localCode.includes('-') ? (
          <span className="text-[11px] mt-1 block" style={{ color: '#92400e', fontFamily: 'Inter, sans-serif' }}>
            Saves as &ldquo;{localCode.split('-')[0].trim()}&rdquo; — booking only ever matches the part before a hyphen.
          </span>
        ) : null}
      </div>
      {/* No Type control on an existing row. Scale does not offer one: the
          rate fields simply swap on the code's own coupon_type, which is fixed
          at creation. A dropdown here implies a fixed-rate code can be turned
          hourly in place, which is not a conversion Scale supports. */}
      {/* A system-linked code is priced per SIDE, not per session length. Scale
          disables the half-day/hourly inputs on these rows and swaps in the
          initiating- and responding-party amounts, because those are the two
          values that actually price it. Showing the session amounts as editable
          would let a case manager change a number that does nothing and be told
          it saved. */}
      {isSystem ? (
        <>
          <div>
            <label className={lbl} style={cstyle}>Initiating Party</label>
            <input className={control} style={cstyle} value={money(local.initiating_party)}
              onChange={(e) => setLocal((p) => ({ ...p, initiating_party: digits(e.target.value) }))}
              onBlur={() => blurSave('initiating_party')} />
          </div>
          <div>
            <label className={lbl} style={cstyle}>Responding Party</label>
            <input className={control} style={cstyle} value={money(local.responding_party)}
              onChange={(e) => setLocal((p) => ({ ...p, responding_party: digits(e.target.value) }))}
              onBlur={() => blurSave('responding_party')} />
          </div>
        </>
      ) : isHourly ? (
        <>
          <div>
            <label className={lbl} style={cstyle}>Hourly Rate</label>
            <input className={control} style={cstyle} value={money(local.hourly_rate)}
              onChange={(e) => setLocal((p) => ({ ...p, hourly_rate: digits(e.target.value) }))}
              onBlur={() => blurSave('hourly_rate')} />
          </div>
          <div>
            <label className={lbl} style={cstyle}>Deposit</label>
            <input className={control} style={cstyle} value={money(local.deposit)}
              onChange={(e) => setLocal((p) => ({ ...p, deposit: digits(e.target.value) }))}
              onBlur={() => blurSave('deposit')} />
          </div>
        </>
      ) : (
        <>
          <div>
            <label className={lbl} style={cstyle}>Half-day Rate</label>
            <input className={control} style={cstyle} value={money(local.half_day_amount)}
              onChange={(e) => setLocal((p) => ({ ...p, half_day_amount: digits(e.target.value) }))}
              onBlur={() => blurSave('half_day_amount')} />
          </div>
          <div>
            <label className={lbl} style={cstyle}>Full-day Rate</label>
            <input className={control} style={cstyle} value={money(local.full_day_amount)}
              onChange={(e) => setLocal((p) => ({ ...p, full_day_amount: digits(e.target.value) }))}
              onBlur={() => blurSave('full_day_amount')} />
          </div>
        </>
      )}
      <div>
        <label className={lbl} style={cstyle}>Processing Fee</label>
        <select className={selectCls} style={cstyle}
          value={String(coupon.is_processing_fee_by_client ?? '-1')}
          onChange={(e) => onPatch(coupon.id, { is_processing_fee_by_client: e.target.value })}>
          {PROCESSING_FEE.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      {/* Copy URL is how a corporate code is actually used: the firm is sent a
          booking link carrying the code. Without it a code created here is
          unusable until someone opens the mediator's own portal to fetch the
          link. Same URL shape as scale-portal's handleCopyURL. */}
      <div className="flex items-end justify-end gap-2">
        <button type="button" onClick={() => onCopyUrl(coupon.coupon_code)}
          className="button button-solid button-sm"
          style={{ fontFamily: 'Inter, sans-serif', background: '#115e59', color: '#fff' }}>
          Copy URL
        </button>
        <button type="button" onClick={onAskDelete}
          className="button button-sm"
          style={{ fontFamily: 'Inter, sans-serif', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}>
          Remove
        </button>
      </div>
    </div>
  );
}
